import { Plugin, PluginKey } from '@tiptap/pm/state';
import { DecorationSet } from '@tiptap/pm/view';
import type { ReviewMode } from '@/lib/ai-composer/types';
import { renderSuggestionDecorations } from './render-modes';
import type { RenderClasses, SuggestionSpec } from './render-modes';

type SuggestionMeta =
  | { type: 'set'; id: string; from: number; to: number; text: string; mode?: ReviewMode; streaming?: boolean; before?: string }
  | { type: 'update'; id: string; text: string; streaming?: boolean; from?: number; to?: number; before?: string }
  | { type: 'clear'; id: string };

export const aiSuggestionPluginKey = new PluginKey<DecorationSet>('aiSuggestion');

export function createSuggestionStorePlugin(
  ghostClass: string,
  ghostStreamingClass: string,
  previewAddedClass?: string,
  reviewAddedClass?: string,
  reviewRemovedClass?: string,
): Plugin<DecorationSet> {
  const classes: RenderClasses = { ghostClass, ghostStreamingClass, previewAddedClass, reviewAddedClass, reviewRemovedClass };

  function addDecorations(set: DecorationSet, doc: Parameters<DecorationSet['add']>[0], spec: SuggestionSpec): DecorationSet {
    const decos = renderSuggestionDecorations(spec, classes);
    return set.add(doc, decos);
  }

  function removeDecorations(set: DecorationSet, id: string): DecorationSet {
    const keys = new Set([
      `ai-${id}`, `ai-${id}-range`, `ai-${id}-shimmer`,
      `ai-${id}-preview`, `ai-${id}-review-added`, `ai-${id}-review-removed`,
    ]);
    return set.remove(
      set.find().filter((d) => keys.has((d.spec as { key?: string }).key ?? '')),
    );
  }

  return new Plugin<DecorationSet>({
    key: aiSuggestionPluginKey,
    state: {
      init: () => DecorationSet.empty,
      apply(tr, set) {
        let next = set.map(tr.mapping, tr.doc);
        const meta = tr.getMeta(aiSuggestionPluginKey) as SuggestionMeta | undefined;
        if (!meta) return next;

        if (meta.type === 'set') {
          next = addDecorations(next, tr.doc, {
            id: meta.id, from: meta.from, to: meta.to,
            text: meta.text, mode: meta.mode ?? 'ghost-write', streaming: meta.streaming,
            before: meta.before,
          });
        } else if (meta.type === 'update') {
          const allDecos = next.find();
          const rangeDec = allDecos.find(
            (d) => (d.spec as { key?: string }).key === `ai-${meta.id}-range`,
          );
          // Mode and before live on the range-tracker spec (recreated each update).
          const rangeSpec = rangeDec?.spec as { mode?: ReviewMode; before?: string } | undefined;
          const existingMode = rangeSpec?.mode ?? 'ghost-write';
          const existingBefore = meta.before ?? rangeSpec?.before;
          next = removeDecorations(next, meta.id);
          const fromPos = meta.from ?? (rangeDec ? rangeDec.from : null);
          const toPos = meta.to ?? (rangeDec ? rangeDec.to : null);
          if (fromPos != null && toPos != null) {
            next = addDecorations(next, tr.doc, {
              id: meta.id, from: fromPos, to: toPos,
              text: meta.text, mode: existingMode, streaming: meta.streaming,
              before: existingBefore,
            });
          }
        } else if (meta.type === 'clear') {
          next = removeDecorations(next, meta.id);
        }
        return next;
      },
    },
    props: {
      decorations(state) {
        return aiSuggestionPluginKey.getState(state);
      },
    },
  });
}
